/**
 * Calculation Engine
 *
 * Purpose: Core business logic for FSS requirement calculations
 * Usage: Calculate requirements based on state and industry selection
 * Features: Handles all special cases, deadlines, and business rules
 */

import { stateData, industryData, businessTypes, trainingUnits } from '../data';

/**
 * Main Calculation Engine Class
 */
class CalculationEngine {
  constructor(stateCode, businessTypeId) {
    this.stateCode = stateCode;
    this.businessTypeId = businessTypeId;
    this.stateConfig = stateData[stateCode];
    this.businessType = businessTypes.find(bt => bt.id === businessTypeId);
    this.industrySector = this.businessType ? industryData[this.businessType.industrySector] : null;
  }

  /**
   * Main calculation method - orchestrates all calculations
   */
  calculate() {
    if (!this.stateConfig || !this.businessType || !this.industrySector) {
      throw new Error('Invalid state or business type provided');
    }

    return {
      compliance: this.getComplianceStatus(),
      training: this.getTrainingRequirements(),
      stateSpecific: this.getStateRequirements(),
      ongoing: this.getOngoingCompliance(),
      warnings: this.getWarnings(),
      links: this.generateLinks()
    };
  }

  /**
   * Get compliance status (FSS required, category, etc.)
   */
  getComplianceStatus() {
    return {
      fssRequired: this.stateConfig.fssRequired,
      fssRequiredNote: this.stateConfig.fssRequiredNote,
      fssRecommended: this.stateConfig.fssRecommended || false,
      state: this.stateCode,
      stateName: this.stateConfig.name,
      businessType: this.businessType.name,
      industry: this.industrySector.name,
      category: this.businessType.category,
      highRisk: this.businessType.highRisk,
      vulnerablePopulation: this.businessType.vulnerablePopulation || false
    };
  }

  /**
   * Get training requirements with state exceptions
   */
  getTrainingRequirements() {
    let units = [...this.industrySector.unitsRequired];
    let skillSetCode = this.industrySector.skillSetCode;
    let notes = [];

    // Check for state exceptions in industry data
    if (this.industrySector.stateExceptions) {
      const stateException = this.industrySector.stateExceptions[this.stateCode];
      const otherException = this.industrySector.stateExceptions.OTHER;

      if (stateException) {
        if (stateException.unitsRequired) {
          units = stateException.unitsRequired;
        }
        if (stateException.note) {
          notes.push(stateException.note);
        }
        if (stateException.requiresWrittenProgram) {
          notes.push('Written Food Safety Program required');
        }
      } else if (otherException && otherException.available === false) {
        // Food processing not available in this state
        units = otherException.alternativeUnits || this.industrySector.alternativeUnits;
        notes.push(otherException.note);
      }
    }

    // Check for business type state restrictions
    if (this.businessType.stateRestricted && this.businessType.stateAvailability) {
      if (!this.businessType.stateAvailability.includes(this.stateCode)) {
        // Use alternative units for food processing outside VIC/QLD
        units = ['SITXFSA005', 'SITXFSA006'];
        notes.push('FBPFSY units not available in your state. SITX units recommended as alternative.');
      }
    }

    // Get detailed unit information
    const unitDetails = units.map(code => trainingUnits[code]).filter(u => u);
    const totalDuration = unitDetails.reduce((sum, unit) => sum + unit.durationHours, 0);

    return {
      units: unitDetails,
      skillSetCode,
      totalDuration,
      assessmentType: unitDetails[0]?.assessmentType || 'Online theory + workplace practical',
      notes,
      alternativeUnits: this.industrySector.alternativeUnits || []
    };
  }

  /**
   * Get state-specific requirements
   */
  getStateRequirements() {
    return {
      validityYears: this.stateConfig.validityYears,
      appointmentDeadlineDays: this.stateConfig.appointmentDeadlineDays,
      displayRequired: this.stateConfig.displayRequired,
      displayDetails: this.stateConfig.displayDetails,
      councilNotification: this.stateConfig.councilNotification,
      councilNotificationDetails: this.stateConfig.councilNotificationDetails,
      standardDeadline: this.stateConfig.standardDeadline,
      standardDeadlineExtended: this.stateConfig.standardDeadlineExtended,
      standard322aApplicable: this.stateConfig.standard322aApplicable,
      auditRequirements: this.stateConfig.auditRequirements,
      focusAreas: this.stateConfig.focusAreas || [],
      specialRequirements: this.stateConfig.specialRequirements || [],
      governingBody: this.stateConfig.governingBody,
      penalties: this.stateConfig.penalties
    };
  }

  /**
   * Get ongoing compliance and recertification requirements
   */
  getOngoingCompliance() {
    return {
      recertificationRequired: this.stateConfig.recertification.required,
      recertificationPeriod: this.stateConfig.validityYears,
      method: this.stateConfig.recertification.method,
      gracePeriodDays: this.stateConfig.recertification.gracePeriodDays,
      durationHours: this.stateConfig.recertification.durationHours,
      fullCourseAlternative: this.stateConfig.recertification.fullCourseAlternative,
      costRange: this.stateConfig.recertification.costRange
    };
  }

  /**
   * Generate warnings based on business rules
   */
  getWarnings() {
    const warnings = [];

    // Check deadline status
    const deadlineCheck = checkDeadlines(this.stateCode, this.stateConfig);
    if (deadlineCheck.warning) {
      warnings.push(deadlineCheck.warning);
    }

    // Victoria special case
    if (this.stateCode === 'VIC' && !this.stateConfig.fssRequired) {
      warnings.push({
        type: 'info',
        message: this.stateConfig.fssRequiredNote,
        title: 'Not Legally Mandated in VIC'
      });
    }

    // High-risk business warning
    if (this.businessType.highRisk) {
      warnings.push({
        type: 'info',
        message: 'Your business is classified as high-risk (Category 1). More stringent compliance requirements apply.',
        title: 'High-Risk Business'
      });
    }

    // Vulnerable population warning
    if (this.businessType.vulnerablePopulation) {
      warnings.push({
        type: 'warning',
        message: 'Your business serves vulnerable populations. Extra care must be taken with food safety compliance.',
        title: 'Vulnerable Population'
      });
    }

    // QLD council notification reminder
    if (this.stateCode === 'QLD' && this.stateConfig.councilNotification) {
      warnings.push({
        type: 'warning',
        message: this.stateConfig.councilNotificationDetails,
        title: 'Council Notification Required'
      });
    }

    // NSW certificate display requirement
    if (this.stateCode === 'NSW' && this.stateConfig.displayRequired) {
      warnings.push({
        type: 'info',
        message: `Certificate must be ${this.stateConfig.displayDetails}`,
        title: 'Display Requirement'
      });
    }

    // Aged care sub-selection requirement
    if (this.businessType.requiresSubSelection) {
      warnings.push({
        type: 'info',
        message: this.businessType.subSelectionNote,
        title: 'Additional Classification Required'
      });
    }

    return warnings;
  }

  /**
   * Generate relevant links for the user
   */
  generateLinks() {
    // Convert industry sector ID to URL-friendly format
    const sectorPath = this.businessType.industrySector
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '');

    return {
      cftCourse: `/food-safety-supervisor/${this.stateCode.toLowerCase()}/${sectorPath}`,
      stateAuthority: this.stateConfig.governingBody.website,
      standard322a: 'https://www.foodstandards.gov.au/sites/default/files/2023-12/Standard%203.2.2A%20Food%20Safety%20Management%20Tools-%20corrected%20271123.pdf',
      foodStandardsAU: 'https://www.foodstandards.gov.au/'
    };
  }
}

/**
 * Check if Standard 3.2.2A deadline has passed
 * @param {string} stateCode - Selected state
 * @param {object} stateConfig - State configuration object
 * @returns {object} Deadline status and warnings
 */
const checkDeadlines = (stateCode, stateConfig) => {
  const today = new Date();
  const deadline = new Date(stateConfig.standardDeadline);
  const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));

  let warning = null;

  if (today > deadline) {
    warning = {
      type: 'error',
      message: `Standard 3.2.2A compliance deadline has passed (${stateConfig.standardDeadline}). Your business may not be compliant.`,
      title: 'Deadline Passed'
    };
  } else if (daysUntilDeadline > 0 && daysUntilDeadline < 90) {
    warning = {
      type: 'warning',
      message: `Standard 3.2.2A compliance deadline is approaching: ${stateConfig.standardDeadline} (${daysUntilDeadline} days remaining)`,
      title: 'Deadline Approaching'
    };
  }

  // Add extended deadline note if applicable
  if (stateConfig.standardDeadlineExtended && warning) {
    warning.message += ' (Extended deadline)';
  }

  return {
    passed: today > deadline,
    deadline: stateConfig.standardDeadline,
    daysRemaining: daysUntilDeadline,
    extended: stateConfig.standardDeadlineExtended,
    warning
  };
};

/**
 * Calculate FSS requirements based on state and business type
 * @param {string} stateCode - Selected state/territory code
 * @param {string} businessTypeId - Selected business type ID
 * @returns {object} Complete requirements object
 */
const calculateRequirements = (stateCode, businessTypeId) => {
  const engine = new CalculationEngine(stateCode, businessTypeId);
  return engine.calculate();
};

/**
 * Validate state and business type combination
 * @param {string} stateCode - State code to validate
 * @param {string} businessTypeId - Business type ID to validate
 * @returns {object} Validation result
 */
const validateSelection = (stateCode, businessTypeId) => {
  const errors = [];

  if (!stateData[stateCode]) {
    errors.push('Invalid state code provided');
  }

  const businessType = businessTypes.find(bt => bt.id === businessTypeId);
  if (!businessType) {
    errors.push('Invalid business type ID provided');
  }

  // Check for state restrictions
  if (businessType && businessType.stateRestricted && businessType.stateAvailability) {
    if (!businessType.stateAvailability.includes(stateCode)) {
      return {
        valid: true,
        requiresAlternative: true,
        message: 'Selected business type has limited training units in your state. Alternative units will be recommended.'
      };
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    requiresAlternative: false
  };
};

export {
  CalculationEngine,
  calculateRequirements,
  checkDeadlines,
  validateSelection
};

export default CalculationEngine;
